import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  Plus, Copy, Check, Trash2, RefreshCw, User, Users,
  Phone, Mail, Shield, ChefHat, ShoppingBag, Truck, Search, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

// Staff role definitions
const STAFF_ROLES = {
  restaurant_manager: {
    label: 'Manager',
    icon: Shield,
    color: 'bg-purple-100 text-purple-700',
    description: 'Full operations access, no billing'
  },
  restaurant_cashier: {
    label: 'Cashier',
    icon: ShoppingBag,
    color: 'bg-green-100 text-green-700',
    description: 'POS and orders only'
  },
  restaurant_kitchen: {
    label: 'Kitchen',
    icon: ChefHat,
    color: 'bg-orange-100 text-orange-700',
    description: 'Kitchen Display System only'
  },
  waiter: {
    label: 'Waiter',
    icon: User,
    color: 'bg-blue-100 text-blue-700',
    description: 'Tables and orders'
  },
  restaurant_driver: {
    label: 'In-House Driver',
    icon: Truck,
    color: 'bg-teal-100 text-teal-700',
    description: 'Deliveries, no documents required'
  }
};

// Permission definitions
const PERMISSION_LABELS = {
  pos: 'POS/Till',
  kds: 'Kitchen Display',
  tables: 'Table Management',
  orders: 'View Orders',
  menu: 'Menu Management',
  staff: 'Staff Management',
  reports: 'Reports',
  settings: 'Settings',
  billing: 'Billing',
  deliveries: 'Deliveries'
};

export default function StaffManagement() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [copiedPin, setCopiedPin] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'waiter'
  });

  const fetchStaff = useCallback(async () => {
    if (!selectedRestaurant?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant?.id]);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      fetchStaff();
    }
  }, [selectedRestaurant?.id, fetchStaff]);

  const handleCreateStaff = async () => {
    if (!form.name || !form.email || !form.role) {
      toast.error('Please fill in name, email, and select a role');
      return;
    }

    try {
      const response = await api.post(`/restaurants/${selectedRestaurant.id}/staff`, form);
      toast.success(
        <div>
          <p>Staff member created!</p>
          <p className="font-mono text-lg mt-1">PIN: {response.data.pin_code}</p>
        </div>,
        { duration: 10000 }
      );
      setShowDialog(false);
      setForm({ name: '', email: '', phone: '', role: 'waiter' });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff member');
    }
  };

  const handleToggleActive = async (member) => {
    try {
      await api.put(`/restaurants/${selectedRestaurant.id}/staff/${member.id}`, {
        is_active: !member.is_active
      });
      toast.success(member.is_active ? 'Staff member deactivated' : 'Staff member activated');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to update staff member');
    }
  };

  const handleRegeneratePin = async (member) => {
    try {
      const response = await api.post(`/restaurants/${selectedRestaurant.id}/staff/${member.id}/regenerate-pin`);
      toast.success(
        <div>
          <p>New PIN generated!</p>
          <p className="font-mono text-lg mt-1">{response.data.pin_code}</p>
        </div>,
        { duration: 10000 }
      );
      fetchStaff();
    } catch (error) {
      toast.error('Failed to regenerate PIN');
    }
  };

  const handleDeleteStaff = async (member) => {
    if (!window.confirm(`Delete ${member.name}? This cannot be undone.`)) return;

    try {
      await api.delete(`/restaurants/${selectedRestaurant.id}/staff/${member.id}`);
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to delete staff member');
    }
  };

  const copyPin = (pin) => {
    navigator.clipboard.writeText(pin);
    setCopiedPin(pin);
    toast.success('PIN copied to clipboard');
    setTimeout(() => setCopiedPin(null), 2000);
  };

  // Filter staff based on search and role
  const filteredStaff = staff.filter(member => {
    const matchesSearch = !searchQuery || 
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    const matchesTab = activeTab === 'all' || member.role === activeTab;
    
    return matchesSearch && matchesRole && matchesTab;
  });

  // Stats by role
  const stats = {
    total: staff.length,
    online: staff.filter(s => s.is_online).length,
    active: staff.filter(s => s.is_active).length,
    byRole: Object.keys(STAFF_ROLES).reduce((acc, role) => {
      acc[role] = staff.filter(s => s.role === role).length;
      return acc;
    }, {})
  };

  const getPin = (member) => member.pin_code || member.waiter_login_code;

  if (loading && staff.length === 0) {
    return (
      <div className="space-y-6" data-testid="staff-management-loading">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-20 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Staff Management</h1>
          <p className="text-slate-500 mt-1">Manage your restaurant team with role-based access</p>
        </div>
        
        <Button onClick={() => setShowDialog(true)} className="btn-primary" data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Staff Member
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-sm text-slate-500">Total Staff</p>
          </CardContent>
        </Card>
        {Object.entries(STAFF_ROLES).slice(0, 4).map(([role, config]) => {
          const Icon = config.icon;
          return (
            <Card key={role}>
              <CardContent className="pt-6 text-center">
                <Icon className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-2xl font-bold text-slate-900">{stats.byRole[role] || 0}</p>
                <p className="text-sm text-slate-500">{config.label}s</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="staff-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="role-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {Object.entries(STAFF_ROLES).map(([role, config]) => (
              <SelectItem key={role} value={role}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Role Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          {Object.entries(STAFF_ROLES).map(([role, config]) => (
            <TabsTrigger key={role} value={role}>
              {config.label} ({stats.byRole[role] || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {/* Staff List */}
          {filteredStaff.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {staff.length === 0 ? 'No staff members yet' : 'No matching staff'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {staff.length === 0 
                    ? 'Add your first staff member to get started' 
                    : 'Try adjusting your search or filter'}
                </p>
                {staff.length === 0 && (
                  <Button onClick={() => setShowDialog(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" /> Add Staff Member
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredStaff.map(member => {
                const roleConfig = STAFF_ROLES[member.role] || STAFF_ROLES.waiter;
                const RoleIcon = roleConfig.icon;
                const pin = getPin(member);
                
                return (
                  <Card 
                    key={member.id} 
                    className={`${!member.is_active ? 'opacity-60' : ''} hover:shadow-md transition-shadow`}
                    data-testid={`staff-${member.id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Staff Info */}
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            member.is_online ? 'bg-green-100' : 'bg-slate-100'
                          }`}>
                            <RoleIcon className={`w-6 h-6 ${member.is_online ? 'text-green-600' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-slate-900">{member.name}</h3>
                              <Badge className={roleConfig.color}>{roleConfig.label}</Badge>
                              {member.is_online && (
                                <Badge className="bg-green-100 text-green-700">Online</Badge>
                              )}
                              {!member.is_active && (
                                <Badge variant="outline" className="text-red-500 border-red-200">Inactive</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Mail className="w-4 h-4" /> {member.email}
                              </span>
                              {member.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" /> {member.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* PIN and Actions */}
                        <div className="flex items-center gap-4 flex-wrap">
                          {/* Login PIN */}
                          {pin && (
                            <div className="bg-slate-100 px-4 py-2 rounded-xl">
                              <p className="text-xs text-slate-500 mb-1">Login PIN</p>
                              <div className="flex items-center gap-2">
                                <code className="text-lg font-mono font-bold text-orange-600">
                                  {pin}
                                </code>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 px-2"
                                  onClick={() => copyPin(pin)}
                                  data-testid={`copy-pin-${member.id}`}
                                >
                                  {copiedPin === pin ? (
                                    <Check className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRegeneratePin(member)}
                              title="Generate new PIN"
                              data-testid={`regenerate-pin-${member.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-500">Active</span>
                              <Switch
                                checked={member.is_active}
                                onCheckedChange={() => handleToggleActive(member)}
                                data-testid={`toggle-active-${member.id}`}
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-500 hover:bg-red-50"
                              onClick={() => handleDeleteStaff(member)}
                              data-testid={`delete-staff-${member.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Permissions Preview */}
                      {member.permissions && Object.keys(member.permissions).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <p className="text-xs text-slate-500 mb-2">Permissions</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(member.permissions)
                              .filter(([_, enabled]) => enabled)
                              .map(([perm, _]) => (
                                <Badge key={perm} variant="outline" className="text-xs">
                                  {PERMISSION_LABELS[perm] || perm}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Staff Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add New Staff Member</DialogTitle>
            <DialogDescription>
              Create a staff account with role-based permissions. They'll receive a 4-digit PIN for login.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* Role Selection */}
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select 
                value={form.role} 
                onValueChange={(value) => setForm(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STAFF_ROLES).map(([role, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={role} value={role}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{config.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {form.role && STAFF_ROLES[form.role] && (
                <p className="text-xs text-slate-500">{STAFF_ROLES[form.role].description}</p>
              )}
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                placeholder="Enter staff member's name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="staff-name-input"
              />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                data-testid="staff-email-input"
              />
              <p className="text-xs text-slate-500">They'll receive their PIN via email</p>
            </div>
            
            {/* Phone */}
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                type="tel"
                placeholder="Enter phone number"
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                data-testid="staff-phone-input"
              />
            </div>

            {/* Role Permissions Preview */}
            {form.role && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Role Permissions</p>
                <div className="flex flex-wrap gap-2">
                  {form.role === 'restaurant_manager' && (
                    <>
                      <Badge className="bg-green-100 text-green-700 text-xs">POS</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">KDS</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Tables</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Orders</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Menu</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Staff</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Reports</Badge>
                    </>
                  )}
                  {form.role === 'restaurant_cashier' && (
                    <>
                      <Badge className="bg-green-100 text-green-700 text-xs">POS</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Orders</Badge>
                    </>
                  )}
                  {form.role === 'restaurant_kitchen' && (
                    <>
                      <Badge className="bg-green-100 text-green-700 text-xs">KDS</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Orders</Badge>
                    </>
                  )}
                  {form.role === 'waiter' && (
                    <>
                      <Badge className="bg-green-100 text-green-700 text-xs">Tables</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Orders</Badge>
                    </>
                  )}
                  {form.role === 'restaurant_driver' && (
                    <>
                      <Badge className="bg-green-100 text-green-700 text-xs">Orders</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Deliveries</Badge>
                      <Badge className="bg-blue-100 text-blue-700 text-xs">No docs required</Badge>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateStaff} 
              className="btn-primary" 
              data-testid="create-staff-btn"
            >
              Create Staff Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
